import { supabase } from '../lib/supabase'

const AVATAR_BUCKET = 'avatars'
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

export interface AvatarUploadResult {
  success: boolean
  path?: string
  url?: string
  error?: string
}

/**
 * Validates an image file for avatar upload
 */
export function validateAvatarFile(file: File): { valid: boolean; error?: string } {
  if (!file) {
    return { valid: false, error: 'No file provided' }
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Please upload a JPEG, PNG, or WebP image.' }
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB` }
  }

  return { valid: true }
}

/**
 * Resizes and compresses an image file for avatar upload
 * Returns a new File object with the processed image
 */
export async function processAvatarImage(file: File, maxSize = 512): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        // Create canvas for resizing
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        // Calculate dimensions (maintain aspect ratio, fit within maxSize square)
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width
            width = maxSize
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height
            height = maxSize
          }
        }

        canvas.width = width
        canvas.height = height

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to process image'))
              return
            }

            // Create new file from blob
            const processedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })

            resolve(processedFile)
          },
          'image/jpeg',
          0.85 // Quality
        )
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target?.result as string
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Uploads an avatar image to Supabase storage
 */
export async function uploadAvatar(userId: string, file: File): Promise<AvatarUploadResult> {
  try {
    // Validate file
    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      return { success: false, error: validation.error }
    }

    // Process image (resize and compress)
    const processedFile = await processAvatarImage(file)

    // Simple filename: user_id.jpg (one avatar per user)
    // This matches the new RLS policy structure
    const fileExt = 'jpg' // Always save as JPEG after processing
    const filePath = `${userId}.${fileExt}`

    // Upload to Supabase
    const { data, error } = await supabase.storage.from(AVATAR_BUCKET).upload(filePath, processedFile, {
      cacheControl: '3600',
      upsert: true,
    })

    if (error) {
      console.error('Upload error:', error)
      return { success: false, error: error.message }
    }

    // Get public URL (since avatars bucket is public)
    const { data: urlData } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(data.path)

    return {
      success: true,
      path: data.path,
      url: urlData.publicUrl,
    }
  } catch (error) {
    console.error('Avatar upload error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload avatar',
    }
  }
}

/**
 * Updates the user's profile with the new avatar URL
 */
export async function updateProfileAvatar(
  userId: string,
  avatarPath: string
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('updateProfileAvatar called:', { userId, avatarPath })

    const { data, error } = await supabase.from('profiles').update({ avatar_url: avatarPath }).eq('id', userId).select()

    console.log('Update response:', { data, error })

    if (error) {
      console.error('Profile update error:', error)
      return { success: false, error: error.message }
    }

    if (!data || data.length === 0) {
      console.warn('No rows updated - this might indicate an RLS policy issue')
      return { success: false, error: 'No rows updated. Check permissions.' }
    }

    console.log('Profile updated successfully:', data)
    return { success: true }
  } catch (error) {
    console.error('Profile update error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update profile',
    }
  }
}

/**
 * Deletes an old avatar from storage
 */
export async function deleteAvatar(avatarPath: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([avatarPath])

    if (error) {
      console.error('Delete error:', error)
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (error) {
    console.error('Delete error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete avatar',
    }
  }
}

/**
 * Gets the public URL for an avatar
 */
export function getAvatarUrl(avatarPath: string | null): string | undefined {
  if (!avatarPath) return undefined

  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(avatarPath)
  return data.publicUrl ?? undefined
}

/**
 * Creates a preview URL from a File object
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

/**
 * Revokes a preview URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url)
}
